import {
  addUserLoggedEventListener,
  addUserLogoutEventListener,
  addAuthenticationBootstrappedEventListener,
} from "./main/user-area/utils/listeners";
import { showLoginModal } from "./main/user-area/utils/loginModal";
import { openConversionModal, setupConversionModal } from "./main/enem-exercises/utils/conversion-modal";

import "./main/enem-exercises";

const desktopBreakpoint = 1024;
const filterDialogState = {
  open: false,
};
const filterRestrictions = {
  active: false,
  premium: {
    active: true,
    fields: ["unique_questions", "has_explanation"],
  },
  logged: {
    active: true,
    fields: ["answer_status"],
  },
};

function setFilterRestrictions(restriction, active) {
  filterRestrictions[restriction].active = active;

  if (active) {
    filterRestrictions[restriction].fields.forEach((field) => {
      const inputs = document.querySelectorAll(`input[name="${field}"]`);
      inputs.forEach((input) => {
        input.checked = false;
      });
    });
  }
}

function openFilterDialog() {
  const filterDialog = document.getElementById("filterDialog");
  filterDialog.showModal();
  filterDialogState.open = true;
}

function closeFilterDialog() {
  const filterDialog = document.getElementById("filterDialog");
  filterDialog.close();
  filterDialogState.open = false;
}

function setupFilterDialog() {
  const filterDialog = document.getElementById("filterDialog");
  const filterTrigger = document.getElementById("filterTrigger");
  const closeDialog = document.getElementById("closeDialog");

  if (!filterDialog || !filterTrigger) return;

  // Show filter dialog on mobile
  filterTrigger.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    openFilterDialog();
  });

  // Close dialog on mobile - click handler for close button
  if (closeDialog) {
    closeDialog.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeFilterDialog();
    });
  }

  // Backdrop click handler
  filterDialog.addEventListener("click", (e) => {
    if (
      filterDialogState.open &&
      e.target === filterDialog &&
      window.innerWidth < desktopBreakpoint
    ) {
      closeFilterDialog();
    }
  });

  // Handle automatic showing of dialog on desktop
  const handleResize = () => {
    if (window.innerWidth >= desktopBreakpoint && filterDialogState.open) {
      // On desktop, make sure dialog is "open" but not as a modal
      closeFilterDialog();
      filterDialog.setAttribute("open", "");
    } else if (
      window.innerWidth < desktopBreakpoint &&
      filterDialog.hasAttribute("open") &&
      !filterDialogState.open
    ) {
      // On mobile, remove "open" if it's not actually open as a modal
      filterDialog.removeAttribute("open");
      closeFilterDialog();
    }
  };

  // Initial check and listen for window resize
  handleResize();
  window.addEventListener("resize", handleResize);
}

function setupFilterFormController() {
  const clearFiltersButton = document.querySelector(
    "button.enem-exercises-filter-actions-button--clear"
  );
  const form = document.querySelector("form.enem-exercises-filter-form");

  const schoolYearInput = document.getElementById("school-year-select");
  const subjectInput = document.getElementById("subject-select");
  const subjectCategoriesInput = document.getElementById(
    "subject-categories-select"
  );
  const difficultyInput = document.getElementById("difficulty-select");
  const uniqueQuestionsInput = document.querySelector(
    'input[name="unique_questions"]'
  );
  const hasExplanationInput = document.querySelector(
    'input[name="has_explanation"]'
  );
  const statusUnansweredInput = document.querySelector(
    'input[name="answer_status"][value="1"]'
  );
  const statusAnsweredInput = document.querySelector(
    'input[name="answer_status"][value="2"]'
  );
  const statusCorrectInput = document.querySelector(
    'input[name="answer_status"][value="3"]'
  );
  const statusIncorrectInput = document.querySelector(
    'input[name="answer_status"][value="4"]'
  );

  const appliedFilters = {
    subject: subjectInput.selected,
    subject_categories: subjectCategoriesInput.selected,
    ...(schoolYearInput ? { school_year_id: schoolYearInput.selected } : {}),
    difficulty: difficultyInput?.selected ?? '',
    unique_questions: uniqueQuestionsInput.checked,
    has_explanation: hasExplanationInput.checked,
    answer_status:
      (statusUnansweredInput.checked && statusUnansweredInput.value) ||
      (statusAnsweredInput.checked && statusAnsweredInput.value) ||
      (statusCorrectInput.checked && statusCorrectInput.value) ||
      (statusIncorrectInput.checked && statusIncorrectInput.value),
  };

  function resetAppliedFilters() {
    const inputs = Object.keys(appliedFilters);
    inputs.forEach((input) => {
      updateAppliedFilters(input, false);
    });
  }

  // Extract numeric ID from a value that may be "id|slug" or just "id"
  function extractId(value) {
    if (!value) return value;
    return String(value).split("|")[0];
  }

  function getCurrentSchoolYearIds() {
    // All-years page includes `school-year-select`. ENEM page doesn't, so backend defaults to ENEM year.
    if (!schoolYearInput) return null;

    // When a specific year is selected, the component renders a hidden input:
    // <input type="hidden" name="school_year_id" value="id|url" />
    const hiddenSchoolYearInput = document.querySelector(
      'input[name="school_year_id"]'
    );
    const selectedYearRaw = hiddenSchoolYearInput?.value ?? "";
    const selectedYearId = extractId(selectedYearRaw);

    if (selectedYearId) return [selectedYearId];

    // "All years" — use the pre-rendered list of all year IDs from PHP
    try {
      const allYearIds = JSON.parse(schoolYearInput.getAttribute("data-all-year-ids") || "[]");
      if (allYearIds.length > 0) return allYearIds.map(String);
    } catch {
      // ignore
    }

    return [];
  }

  function updateAppliedFilters(name, value) {
    appliedFilters[name] = value;
    const hasFiltersApplied = Object.values(appliedFilters).some(
      (value) => !!value
    );
    clearFiltersButton.classList.toggle("hidden", !hasFiltersApplied);
  }

  function handleFormSubmit() {
    // Build the URL from the form's actual state (FormData). The components
    // only render hidden inputs for non-empty selections, so no extra gating
    // is needed — gating on appliedFilters (event-driven bookkeeping) used to
    // drop filters when duplicated change events left it out of sync.
    const formData = new FormData(form);
    const url = new URL(form.action, window.location.origin);
    const isQuestoesPage = form.dataset.pageType === 'all-years';
    url.pathname = url.pathname.replace(/\/$/, '') + '/';

    const selectedSubject = formData.get("subject");
    if (selectedSubject) {
      const [_, subjectSlug] = selectedSubject.split("|");
      if (subjectSlug) {
        url.pathname = `${url.pathname}${subjectSlug}/`;
      }
    }

    // On questoes page, school year always goes in URL path
    // /questoes/matematica/6-ano/ (with subject) or /questoes/6-ano/ (year only)
    const selectedSchoolYear = formData.get("school_year_id");
    if (isQuestoesPage && selectedSchoolYear) {
      const parts = selectedSchoolYear.split("|");
      if (parts.length === 2) {
        const yearSlug = parts[1];
        url.pathname = url.pathname.replace(/\/$/, '') + '/' + yearSlug + '/';
      }
    }

    formData.delete("subject");
    formData.forEach((value, key) => {
      if (!value) return;
      // On questoes page, school_year_id is always in the path, never a query param
      if (key === 'school_year_id' && isQuestoesPage) return;
      url.searchParams.set(key, value)
    });

    if (window.innerWidth < desktopBreakpoint && filterDialogState.open) {
      closeFilterDialog();
    }

    if (window.location.href !== url.toString()) {
      window.location.href = url.toString();
    }
  }

  clearFiltersButton.addEventListener("click", () => {
    form.reset();
    uniqueQuestionsInput.checked = false;
    hasExplanationInput.checked = false;
    statusUnansweredInput.checked = false;
    statusAnsweredInput.checked = false;
    statusCorrectInput.checked = false;
    statusIncorrectInput.checked = false;
    resetAppliedFilters();

    // Don't go through handleFormSubmit() here: the searchable-select
    // components clear their state on the form's `reset` event, but their
    // hidden inputs are only removed on the next Lit render, so a synchronous
    // FormData(form) would still carry every filter. Every field in this form
    // is a filter and `action` is the unfiltered listing (/questoes/ or
    // /exercicios-enem/), so the cleared URL is just the form action.
    const clearedUrl = new URL(form.action, window.location.origin);
    clearedUrl.pathname = clearedUrl.pathname.replace(/\/$/, "") + "/";

    if (window.innerWidth < desktopBreakpoint && filterDialogState.open) {
      closeFilterDialog();
    }

    if (window.location.href !== clearedUrl.toString()) {
      window.location.href = clearedUrl.toString();
    }
  });

  function onSelectInputChanged(e) {
    if (!e.detail) return;
    const { name, value } = e.detail;

    if (typeof value === "string") {
      return updateAppliedFilters(name, !!value);
    } else if (typeof value === "object") {
      return updateAppliedFilters(name, value.length > 0);
    }

    return updateAppliedFilters(name, false);
  }

  subjectInput.addEventListener("initialized", onSelectInputChanged);
  // Track in-flight subject-change requests so duplicate events are harmless.
  let subjectChangeController = null;
  subjectInput.addEventListener("change", async (e) => {
    onSelectInputChanged(e);
    if (!e.detail) return;
    updateAppliedFilters("subject_categories", false);
    const selectedSubject = e.detail.value;

    subjectCategoriesInput.toggleAttribute("disabled", true);
    subjectCategoriesInput.options = JSON.stringify([]);
    subjectCategoriesInput.disabledMessage =
      "Selecione uma disciplina primeiro";
    if (!selectedSubject) return;

    // Cancel any in-flight request from a previous (duplicate) change event.
    if (subjectChangeController) {
      subjectChangeController.abort();
    }
    subjectChangeController = new AbortController();
    const { signal } = subjectChangeController;

    subjectCategoriesInput.toggleAttribute("data-loading", true);
    const [subjectId, _] = selectedSubject.split("|");

    const schoolYearIds = getCurrentSchoolYearIds();
    const query = new URLSearchParams({
      action: "taxonomy-temas",
      subject_id: subjectId,
      // Tells the API which listing is asking: /questoes/ ("all-years") hides
      // temas whose only questions are past-ENEM ones, /exercicios-enem/ shows them.
      page_type: form.dataset.pageType || "",
    });
    if (Array.isArray(schoolYearIds) && schoolYearIds.length > 0) {
      schoolYearIds.forEach((id) => query.append("year_ids[]", id));
    }

    try {
      const response = await fetch(`/api/exercises/?${query.toString()}`, { signal });
      if (!response.ok) {
        subjectCategoriesInput.toggleAttribute("data-loading", false);
        subjectCategoriesInput.disabledMessage =
          "Não foi possível carregar as categorias";
        return;
      }

      const {
        data: { subjectCategoryOptions },
      } = await response.json();

      subjectCategoriesInput.options = JSON.stringify(subjectCategoryOptions);
      subjectCategoriesInput.toggleAttribute("data-loading", false);
      subjectCategoriesInput.disabledMessage =
        subjectCategoryOptions.length > 0 ? "" : "Nenhum assunto disponível";
      subjectCategoriesInput.toggleAttribute(
        "disabled",
        subjectCategoryOptions.length === 0
      );
    } catch (err) {
      if (err.name === "AbortError") return; // Superseded by a newer request
      subjectCategoriesInput.toggleAttribute("data-loading", false);
      subjectCategoriesInput.disabledMessage =
        "Não foi possível carregar as categorias";
    }
  });

  // When the year changes, categories should be reloaded for the currently selected subject.
  // We need to get the school year ID from the event detail because the hidden input
  // rendered by the web component may not yet be updated when the change event fires.
  let schoolYearChangeController = null;
  schoolYearInput?.addEventListener("change", async (e) => {
    const hiddenSubjectInput = document.querySelector('input[name="subject"]');
    const subjectValue = hiddenSubjectInput?.value ?? "";
    if (!subjectValue) return;

    // The searchable-select component can fire duplicate change events (label click + input change).
    // Cancel any in-flight request so only the last one wins.
    if (schoolYearChangeController) {
      schoolYearChangeController.abort();
    }
    schoolYearChangeController = new AbortController();
    const { signal } = schoolYearChangeController;

    updateAppliedFilters("subject_categories", false);
    subjectCategoriesInput.toggleAttribute("disabled", true);
    subjectCategoriesInput.options = JSON.stringify([]);
    subjectCategoriesInput.disabledMessage = "Carregando assuntos...";

    subjectCategoriesInput.toggleAttribute("data-loading", true);
    const [subjectId, _] = subjectValue.split("|");

    // Get the school year ID from the event detail value instead of the hidden input,
    // because LitElement may not have re-rendered the hidden input yet at this point.
    const selectedYearValue = e.detail?.value ?? "";
    const selectedYearId = extractId(selectedYearValue);
    let schoolYearIds;
    if (selectedYearId) {
      schoolYearIds = [selectedYearId];
    } else {
      // "All years" selected — use the pre-rendered list
      try {
        const allYearIds = JSON.parse(schoolYearInput.getAttribute("data-all-year-ids") || "[]");
        schoolYearIds = allYearIds.length > 0 ? allYearIds.map(String) : [];
      } catch {
        schoolYearIds = [];
      }
    }

    const query = new URLSearchParams({
      action: "taxonomy-temas",
      subject_id: subjectId,
      // Tells the API which listing is asking: /questoes/ ("all-years") hides
      // temas whose only questions are past-ENEM ones, /exercicios-enem/ shows them.
      page_type: form.dataset.pageType || "",
    });
    if (Array.isArray(schoolYearIds) && schoolYearIds.length > 0) {
      schoolYearIds.forEach((id) => query.append("year_ids[]", id));
    }

    try {
      const response = await fetch(`/api/exercises/?${query.toString()}`, { signal });
      if (!response.ok) {
        subjectCategoriesInput.toggleAttribute("data-loading", false);
        subjectCategoriesInput.disabledMessage =
          "Não foi possível carregar as categorias";
        return;
      }

      const {
        data: { subjectCategoryOptions },
      } = await response.json();

      subjectCategoriesInput.options = JSON.stringify(subjectCategoryOptions);
      subjectCategoriesInput.toggleAttribute("data-loading", false);
      subjectCategoriesInput.disabledMessage =
        subjectCategoryOptions.length > 0 ? "" : "Nenhum assunto disponível";
      subjectCategoriesInput.toggleAttribute(
        "disabled",
        subjectCategoryOptions.length === 0
      );
    } catch (err) {
      if (err.name === "AbortError") return; // Superseded by a newer request
      subjectCategoriesInput.toggleAttribute("data-loading", false);
      subjectCategoriesInput.disabledMessage =
        "Não foi possível carregar as categorias";
    }
  });

  subjectCategoriesInput.addEventListener("initialized", onSelectInputChanged);
  subjectCategoriesInput.addEventListener("change", onSelectInputChanged);

  schoolYearInput?.addEventListener("initialized", onSelectInputChanged);
  schoolYearInput?.addEventListener("change", onSelectInputChanged);
  difficultyInput?.addEventListener("initialized", onSelectInputChanged);
  difficultyInput?.addEventListener("change", onSelectInputChanged);

  function onCheckboxInputChanged(e) {
    const { name, checked } = e.target;
    updateAppliedFilters(name, checked);
  }

  function onRadioInputChanged(e) {
    const { name, value, checked } = e.target;
    updateAppliedFilters(name, checked ? value : false);
  }

  function checkIfRestrictionsAreActive(e) {
    if (!filterRestrictions.active) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    return true;
  }

  function showLoginModalIfRestricted(e) {
    if (!checkIfRestrictionsAreActive(e)) return false;
    
    const inputName = e.target.name;
    const isRestricted = filterRestrictions.logged.active && filterRestrictions.logged.fields.includes(inputName);
    if (isRestricted) {
      e.preventDefault();
      e.stopPropagation();
      let callback = undefined;

      if (filterDialogState.open) {
        closeFilterDialog();
        callback = openFilterDialog;
      }

      showLoginModal("Faça login para liberar mais filtros", callback);
      return false;
    }

    return true;
  }

  function showPremiumModalIfRestricted(e) {
    if (!checkIfRestrictionsAreActive(e)) return;

    const inputName = e.target.name;
    const isRestricted = filterRestrictions.premium.active && filterRestrictions.premium.fields.includes(inputName);
    if (isRestricted) {
      e.preventDefault();
      e.stopPropagation();

      if (filterDialogState.open) {
        closeFilterDialog();
      }

      // Sem evento próprio no filtro: a popup abre logo a seguir e o seu
      // funnel_cta_view (element com filter_exclusive/filter_gabarito) conta a
      // mesma interação — o paywall_premium_filter duplicava-o e ocupava as
      // custom dimensions filter_name e page_section.
      const context = inputName === 'unique_questions' ? 'filter-exclusive' : 'filter-gabarito';
      openConversionModal(context);
      return false;
    }

    return true;
  }

  function toggleQuestionStatusRadioInput(e, preventDefault = false) {
    const { name, value, checked } = e.target;
    if (checked && value === appliedFilters.answer_status) {
      if (preventDefault) {
        e.preventDefault();
      }
      e.target.checked = false;
      updateAppliedFilters(name, false);
    }
  }

  function handleQuestionStatusRadioInputKeydown(e) {
    if (e.code !== "Space") return;
    toggleQuestionStatusRadioInput(e, true);
  }

  function onQuestionStatusRadioInputClick(e) {
    if (showLoginModalIfRestricted(e)) {
      toggleQuestionStatusRadioInput(e);
    }
  }

  uniqueQuestionsInput.addEventListener("change", onCheckboxInputChanged);
  uniqueQuestionsInput.addEventListener("click", showPremiumModalIfRestricted);

  hasExplanationInput.addEventListener("change", onCheckboxInputChanged);
  hasExplanationInput.addEventListener("click", showPremiumModalIfRestricted);

  statusUnansweredInput.addEventListener("change", onRadioInputChanged);
  statusUnansweredInput.addEventListener(
    "click",
    onQuestionStatusRadioInputClick
  );
  statusUnansweredInput.addEventListener(
    "keydown",
    handleQuestionStatusRadioInputKeydown
  );

  statusAnsweredInput.addEventListener("change", onRadioInputChanged);
  statusAnsweredInput.addEventListener(
    "click",
    onQuestionStatusRadioInputClick
  );
  statusAnsweredInput.addEventListener(
    "keydown",
    handleQuestionStatusRadioInputKeydown
  );

  statusCorrectInput.addEventListener("change", onRadioInputChanged);
  statusCorrectInput.addEventListener("click", onQuestionStatusRadioInputClick);
  statusCorrectInput.addEventListener(
    "keydown",
    handleQuestionStatusRadioInputKeydown
  );

  statusIncorrectInput.addEventListener("change", onRadioInputChanged);
  statusIncorrectInput.addEventListener(
    "click",
    onQuestionStatusRadioInputClick
  );
  statusIncorrectInput.addEventListener(
    "keydown",
    handleQuestionStatusRadioInputKeydown
  );

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    handleFormSubmit();
  });

  // Badge-lock pill click handlers
  const premiumLockButtons = document.querySelectorAll('.enem-exercises-filter-premium-lock');
  premiumLockButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (filterDialogState.open) {
        closeFilterDialog();
      }
      const context = btn.dataset.context;
      openConversionModal(context);
    });
  });
}

function setupUpsellBanner() {
  const unlockBtn = document.querySelector('.enem-exercises-results-count-unlock');
  if (!unlockBtn) return;

  unlockBtn.addEventListener('click', (e) => {
    e.preventDefault();
    // O funnel_cta_view da popup (element …_banner_unlock) substitui o antigo
    // paywall_upsell_banner_click — mesma interação, sem gastar banner_variant
    // nem page_section.
    openConversionModal('banner-unlock');
  });
}

document.addEventListener("DOMContentLoaded", () => {
  setupFilterDialog();
  setupFilterFormController();
  setupConversionModal();
  setupUpsellBanner();
});

addUserLoggedEventListener((event) => {
  const { isPremium, uuid } = event.detail;
  setFilterRestrictions("logged", !!!uuid);
  setFilterRestrictions("premium", !(!!uuid && isPremium));

  // Toggle badge-locks vs checkboxes
  const locks = document.getElementById('premium-filter-locks');
  const checks = document.getElementById('premium-filter-checks');
  if (locks && checks) {
    const showCheckboxes = !!uuid && isPremium;
    locks.hidden = showCheckboxes;
    checks.hidden = !showCheckboxes;
  }
});

addUserLogoutEventListener(() => {
  setFilterRestrictions("logged", true);
  setFilterRestrictions("premium", true);

  // Show badge-locks, hide checkboxes
  const locks = document.getElementById('premium-filter-locks');
  const checks = document.getElementById('premium-filter-checks');
  if (locks && checks) {
    locks.hidden = false;
    checks.hidden = true;
  }
});

addAuthenticationBootstrappedEventListener(() => {
  filterRestrictions.active = true;
});
